/**
 * @fileoverview Slice Redux centralizado para dados globais (products, categories, brands, promotions).
 * Single source of truth — substitui leitura direta do localStorage/memoryCache.
 * Hidrata estado inicial a partir do localStorage para evitar flash de conteúdo vazio.
 *
 * @module globalDataSlice
 * @since 2.0.0
 */

import { createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';

/**
 * Chaves do localStorage para hidratação inicial.
 */
const STORAGE_KEYS = {
  PRODUCTS: 'vikanaweb_products',
  CATEGORIES: 'vikanaweb_categories',
  BRANDS: 'vikanaweb_brands',
  PROMOTIONS: 'vikanaweb_promotions',
};

/**
 * Lê dados do localStorage de forma segura.
 * @param {string} key - Chave do localStorage
 * @returns {Array} Dados parseados ou array vazio
 */
const hydrateFromStorage = (key) => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};
 
/**
 * Estado inicial hidratado a partir do localStorage.
 */
const initialState = {
  products: hydrateFromStorage(STORAGE_KEYS.PRODUCTS),
  categories: hydrateFromStorage(STORAGE_KEYS.CATEGORIES),
  brands: hydrateFromStorage(STORAGE_KEYS.BRANDS),
  promotions: hydrateFromStorage(STORAGE_KEYS.PROMOTIONS),
  lastSync: null,
  syncLoading: false,
  syncError: null,
};

const globalDataSlice = createSlice({
  name: 'globalData',
  initialState,
  reducers: {
    /**
     * Atualiza produtos no estado global.
     * @param {Object} state
     * @param {{ payload: Array }} action
     */
    setProducts: (state, action) => {
      state.products = action.payload || [];
    },
    /**
     * Atualiza categorias no estado global.
     * @param {Object} state
     * @param {{ payload: Array }} action
     */
    setCategories: (state, action) => {
      state.categories = action.payload || [];
    },
    /**
     * Atualiza marcas no estado global.
     * @param {Object} state
     * @param {{ payload: Array }} action
     */
    setBrands: (state, action) => {
      state.brands = action.payload || [];
    },
    /**
     * Atualiza promoções no estado global.
     * @param {Object} state
     * @param {{ payload: Array }} action
     */
    setPromotions: (state, action) => {
      state.promotions = action.payload || [];
    },
    /**
     * Marca início da sincronização.
     */
    syncStarted: (state) => {
      state.syncLoading = true;
      state.syncError = null;
    },
    /**
     * Marca fim da sincronização com sucesso.
     */
    syncCompleted: (state) => {
      state.syncLoading = false;
      state.lastSync = new Date().toISOString();
    },
    /**
     * Marca fim da sincronização com erro.
     * @param {Object} state
     * @param {{ payload: string }} action
     */
    syncFailed: (state, action) => {
      state.syncLoading = false;
      state.syncError = action.payload;
    },
  },
});

export const {
  setProducts,
  setCategories,
  setBrands,
  setPromotions,
  syncStarted,
  syncCompleted,
  syncFailed,
} = globalDataSlice.actions;

// --- Selectors ---

/**
 * Seleciona todos os produtos (sem filtro).
 * @param {Object} state
 * @returns {Array}
 */
export const selectProducts = (state) => state.globalData.products;

/**
 * Seleciona apenas produtos ativos e não removidos — memoizado.
 * @type {import('reselect').OutputSelector}
 */
export const selectActiveProducts = createSelector(
  (state) => state.globalData.products,
  (products) =>
    // filter: exclui produtos inativos ou removidos da exibição pública
    products.filter(
      (p) => p.ativo !== false && p.ativo !== 0 && p.removido !== true && p.removido !== 1
    )
);

/**
 * Seleciona todas as categorias.
 * @param {Object} state
 * @returns {Array}
 */
export const selectCategories = (state) => state.globalData.categories;

/**
 * Seleciona todas as marcas.
 * @param {Object} state
 * @returns {Array}
 */
export const selectBrands = (state) => state.globalData.brands;

/**
 * Seleciona todas as promoções.
 * @param {Object} state
 * @returns {Array}
 */
export const selectPromotions = (state) => state.globalData.promotions;

/**
 * Factory de selector memoizado por productId.
 * Retorna sempre a mesma instância para o mesmo id, evitando re-renders.
 * @param {string|number} productId
 * @returns {import('reselect').OutputSelector}
 */
export const selectProductById = (productId) =>
  createSelector(
    (state) => state.globalData.products,
    (products) => products.find((p) => p.id == productId) || null
  );

/**
 * Seleciona estado de sincronização.
 * @param {Object} state
 * @returns {Object}
 */
export const selectSyncState = (state) => ({
  loading: state.globalData.syncLoading,
  error: state.globalData.syncError,
  lastSync: state.globalData.lastSync,
});

export default globalDataSlice.reducer;
